import { AssetTypeId, StatusCode } from '@/modules/asset/constants';
import { Asset, InputAsset } from '@/modules/asset/types';
import { computed, Ref } from '@vue/composition-api';
import { AccessLevel } from '@/modules/access-policy/constants';

export function useMarketplace(
    asset: Ref<Asset | undefined>,
    user: Ref<{ id: number; organisationId: number }>,
    inputAssets: Ref<InputAsset[]>,
) {
    const canAddToMarketplace = computed(
        () =>
            asset.value &&
            [AssetTypeId.Result, AssetTypeId.Model].includes(asset.value.assetTypeId) &&
            asset.value.createdBy.organisationId === user.value.organisationId &&
            [StatusCode.Uploading, StatusCode.Incomplete, StatusCode.Internal].includes(asset.value.status),
    );

    const hasNoActiveAcquisitionContracts = computed(() =>
        inputAssets.value.some((inputAsset) => !inputAsset.isAcquired),
    );

    const isAddToMarketplaceDisabled = computed(
        () =>
            !asset.value ||
            asset.value.status !== StatusCode.Internal ||
            hasNoActiveAcquisitionContracts.value ||
            asset.value.accessLevel === AccessLevel.OrganisationLevel,
    );

    const addToMarketplaceTooltip = computed(() => {
        if (asset.value?.status === StatusCode.Incomplete)
            return 'Dataset metadata need to be filled in before it can be added to the marketplace.';
        if (asset.value?.accessLevel === AccessLevel.OrganisationLevel)
            return 'This dataset has Organisation access level. To add it to the marketplace, change the access level to Selective Sharing.';
        if (hasNoActiveAcquisitionContracts.value)
            return 'Some acquisition contracts are not active or expired and must be activated before this dataset can be added to the marketplace.';
        if (asset.value?.status === StatusCode.Internal)
            return 'Add this dataset to the marketplace. In case of derivative dataset, derivation contract requests will be sent to all dataset providers.';
        return null;
    });

    return {
        canAddToMarketplace,
        isAddToMarketplaceDisabled,
        addToMarketplaceTooltip,
    };
}
